/**
 * Der einzige Vertrag zwischen Simulation und Außenwelt.
 *
 * Hier stehen ausschließlich Datentypen — keine Logik, keine Abhängigkeiten.
 * Renderer, Server und Spielstand kennen nur diese Typen, nie den inneren
 * Weltzustand von `@pg/sim`. Siehe docs/2026-07-25-architektur.md §3.
 */

export interface Vek {
  x: number;
  y: number;
}

// ---------------------------------------------------------------- Eingaben

/**
 * Was der Spieler will. Automatik und Handeingabe erzeugen denselben Typ —
 * die Simulation weiß nicht, ob ein Mensch oder eine Regel ausgelöst hat.
 * Das ist Voraussetzung dafür, dass Coop niemanden bevorzugt und dass der
 * Balance-Runner ganze Builds ohne Spieler durchspielen kann.
 */
/**
 * Wie weit die Automatik gehen darf.
 *   'aus'          — nichts feuert von selbst
 *   'grundangriff' — nur der Grundangriff (Platz 1). Standard.
 *   'freigegeben'  — zusätzlich die Fähigkeiten, die der Spieler freigegeben hat
 */
export type Automatikgrad = 'aus' | 'grundangriff' | 'freigegeben';

export type Absicht =
  /** Richtung normalisiert, oder null für „keine Eingabe". */
  | { art: 'bewegen'; richtung: Vek | null }
  | { art: 'autolauf'; an: boolean }
  | { art: 'anker'; an: boolean }
  /** Manuelle Übersteuerung: drängt sich vor die Automatik. */
  | { art: 'faehigkeit'; slot: number; ziel: Vek | null }
  /** Wie weit die Automatik gehen darf. Siehe Ueberarbeitung B9a. */
  | { art: 'automatikgrad'; grad: Automatikgrad }
  /** Einzelne Fähigkeit auf Handbetrieb stellen (feuert nie von selbst). */
  | { art: 'handbetrieb'; slot: number; an: boolean }
  /** Antwort auf eine offene Freischaltung. */
  | { art: 'waehleFaehigkeit'; faehigkeit: string }
  /** Gangart umschalten — zum Vergleichen im laufenden Spiel. */
  | { art: 'tempoprofil'; profil: string }
  /** In den Förderkorb steigen und den Abstieg beenden. Bewusst ein Klick. */
  | { art: 'einsteigen' }
  | { art: 'aufheben'; id: number };

// ---------------------------------------------------------------- Sichten

export type WesenArt = 'spieler' | 'gegner' | 'begleiter';
export type Haltung = 'stehen' | 'gehen' | 'schlagen' | 'getroffen' | 'sterben';

export type Rang = 'gewoehnlich' | 'vorarbeiter' | 'gezeichneter' | 'ausklang';

export interface WesenSicht {
  id: number;
  art: WesenArt;
  /** Rang — steuert Darstellung (Größe, Lebensbalken, Namensschild). */
  rang: Rang;
  /** Anzeigename inkl. Rang und Merkmalen. Leer bei gewöhnlichen Gegnern. */
  anzeigename: string;
  /** Verweis auf die Inhaltsdefinition (Gegnerart, Klasse). */
  bauplan: string;
  pos: Vek;
  /** Blickrichtung in Radiant. */
  winkel: number;
  radius: number;
  haltung: Haltung;
  /** 0..1 — für Lebensbalken. */
  leben: number;
  lebenMax: number;
  /** true bei benannten Gegnern und Eliten. */
  benannt: boolean;
  /** Fortschritt der laufenden Aktion, 0..1 — treibt die Animation. */
  aktion: number;
  /** Vorlauf eines telegrafierten Angriffs in Sekunden, 0 = keiner. */
  telegraf: number;
}

export type Seltenheit = 'schlicht' | 'gezeichnet' | 'verschriftet' | 'gepraegt' | 'reliquie';

export interface BeuteSicht {
  id: number;
  pos: Vek;
  seltenheit: Seltenheit;
  name: string;
  /** Baugut, Siegel und Material werden anders dargestellt als Ausrüstung. */
  gattung: 'ausruestung' | 'siegel' | 'baugut' | 'material' | 'gold' | 'fladen';
  menge: number;
  /**
   * true, wenn der Spieler es anklicken muss. Gold und Fladen werden im
   * Vorbeigehen genommen, Beute nicht — sie soll ein Ereignis sein.
   */
  anklicken: boolean;
  /** Ist der Spieler nah genug zum Aufheben? */
  inReichweite: boolean;
}

export interface GeschossSicht {
  id: number;
  bauplan: string;
  pos: Vek;
  winkel: number;
}

export type EffektArt =
  | 'treffer'
  | 'kritisch'
  | 'tod'
  | 'schlag'
  | 'aufgehoben'
  | 'stufe'
  | 'telegraf';

export interface EffektSicht {
  art: EffektArt;
  pos: Vek;
  /** Schadenszahl, Betrag oder Radius — je nach Art. */
  wert: number;
  /** Takt, in dem der Effekt entstand — der Renderer blendet selbst aus. */
  takt: number;
}

export interface AbstiegSicht {
  region: string;
  tiefe: number;
  grad: number;
  /** 'laeuft' | 'geschafft' | 'gescheitert' */
  stand: 'laeuft' | 'geschafft' | 'gescheitert';
  aufgabe: {
    text: string;
    fortschritt: number;
    ziel: number;
  };
  /** Position des Abschlusses, falls schon bekannt — für den Kompass. */
  ziel: Vek | null;
  /** Der Förderkorb, sobald er unten ist. Erst das Einsteigen beendet den Abstieg. */
  ausgang: Vek | null;
  /** Steht der Spieler nah genug, um einzusteigen? */
  ausgangInReichweite: boolean;
}

export interface SpielerSicht extends WesenSicht {
  stufe: number;
  erfahrung: number;
  erfahrungNaechste: number;
  vorrat: number;
  vorratMax: number;
  gold: number;
  /** Zustand der fünf Fähigkeitsplätze. */
  slots: SlotSicht[];
  autolauf: boolean;
  anker: boolean;
  automatikgrad: Automatikgrad;
  /** Gangart: 'flink' oder 'gemessen'. */
  tempoprofil: string;
  /** Freigeschaltete Fähigkeiten (Ueberarbeitung B10). */
  freigeschaltet: string[];
  /** Offene Freischaltung: Angebote, aus denen gewählt werden muss. */
  offeneWahl: FaehigkeitsAngebot[] | null;
}

export interface FaehigkeitsAngebot {
  id: string;
  name: string;
  beschreibung: string;
  /** true, wenn sie den Spieler versetzt — für die Anzeige wichtig. */
  bewegtSpieler: boolean;
}

export interface SlotSicht {
  slot: number;
  faehigkeit: string;
  name: string;
  /** true = feuert nie von selbst (vom Spieler gesetzt). */
  handbetrieb: boolean;
  /** false = darf gar nicht automatisch feuern, weil sie anhält oder versetzt. */
  automatikfaehig: boolean;
  /** true = der Grundangriff auf Platz 1. */
  grundangriff: boolean;
  kategorie: string;
  /** true = versetzt den Spieler; im Grad „umsichtig" nie automatisch. */
  bewegtSpieler: boolean;
  /** Reicht der Vorrat gerade? */
  bezahlbar: boolean;
  /** 0 = bereit, sonst verbleibende Sekunden. */
  abklingen: number;
  abklingenMax: number;
  /** true, wenn die Automatik diese Fähigkeit gerade auslösen würde. */
  regelAktiv: boolean;
  /** true, solange die manuelle Übersteuerung wirkt. */
  manuell: boolean;
}

export interface Schnappschuss {
  takt: number;
  /** Sekunden seit Beginn des Abstiegs. */
  zeit: number;
  spieler: SpielerSicht;
  wesen: WesenSicht[];
  beute: BeuteSicht[];
  geschosse: GeschossSicht[];
  /** Nur die Effekte dieses Takts. Der Renderer sammelt selbst. */
  effekte: EffektSicht[];
  abstieg: AbstiegSicht;
}

// ---------------------------------------------------------------- Ereignisse

/**
 * Ereignisse sind das, was außerhalb der Simulation interessiert:
 * Ton, Kontoseite, Statistik, Erfolge. Sie sind rein informativ — niemand
 * darf aus ihnen den Weltzustand rekonstruieren wollen.
 */
export type Ereignis =
  | { art: 'wesen-gestorben'; id: number; bauplan: string; benannt: boolean }
  | { art: 'beute-gefallen'; id: number; seltenheit: Seltenheit; name: string }
  | { art: 'beute-aufgehoben'; id: number; name: string; durchBegleiter: boolean }
  | { art: 'spieler-getroffen'; schaden: number; verbleibend: number }
  | { art: 'spieler-gestorben' }
  | { art: 'stufe-erreicht'; stufe: number }
  | { art: 'freischaltung-offen'; angebote: FaehigkeitsAngebot[] }
  | { art: 'faehigkeit-freigeschaltet'; faehigkeit: string; name: string }
  | { art: 'abstieg-geschafft'; dauer: number; beute: number }
  | { art: 'aufgabe-fortschritt'; fortschritt: number; ziel: number };

// ---------------------------------------------------------------- Konstanten

/** Simulationstakt. Zahlen-Kanon §12.4 — nicht ohne Architekturentscheidung ändern. */
export const TAKTE_PRO_SEKUNDE = 30;
export const TAKT_DAUER = 1 / TAKTE_PRO_SEKUNDE;
